/**
 * DevelopmentView — the engineering hub under Projects → Development.
 *
 * Four surfaces in one scroll:
 *   1. North Star  — the single sentence everything ladders up to.
 *   2. Roadmap     — milestones grouped by now / next / later / shipped,
 *                    each with goal, owner, timeline, status, progress.
 *   3. Important docs — config-driven links to specs and references.
 *   4. Agent workers  — live status of the network seams (Claude worker,
 *                    enrichment, GSuite) so it's obvious what's connected.
 *
 * Roadmap data is DB-backed (src/lib/db/roadmap.ts); North Star, docs,
 * and worker links are config-driven constants surfaced here. Magenta /
 * blue / orange accents only; Lucide icons only.
 *
 * Milestones can link tickets + tasks (task↔ticket↔roadmap linking):
 * when links exist the progress bar is auto-computed from completion
 * (src/lib/crm/milestones.ts) and badged "auto" — the roadmap reports
 * reality instead of a hand-set estimate. Linking is staff-gated.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Compass,
  Map as MapIcon,
  FileText,
  Cpu,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  AlertTriangle,
  Calendar,
  CalendarClock,
  User as UserIcon,
  ExternalLink,
  Sparkles,
  Link2,
  Ticket as TicketIcon,
  ListChecks,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { AgentFleetWidget } from '../components/AgentFleetWidget';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  bootstrapRoadmapIfEmpty,
  subscribeRoadmap,
  updateRoadmapItem,
} from '../lib/db/roadmap';
import {
  milestoneProgress,
  milestoneRollup,
  type LinkedWorkRef,
} from '../lib/crm/milestones';
import { subscribeTickets } from '../lib/db/tickets';
import { subscribeTasks } from '../lib/db/tasks';
import type {
  MilestoneStatus,
  ProjectTask,
  RoadmapItem,
  RoadmapStage,
  Ticket,
} from '../lib/db/types';
import * as argoWorker from '../lib/agent/argoWorker';
import * as enrich from '../lib/agent/enrich';
import * as meetNotes from '../lib/agent/meetNotes';
import * as slackNotify from '../lib/agent/slackNotify';

// --- Static config ----------------------------------------------------------

const NORTH_STAR =
  'Deploy an AI operating layer that compounds every team’s output — measured ' +
  'by a single rising AQI score — instead of adding headcount.';

interface DocLink {
  title: string;
  description: string;
  url: string;
}

const IMPORTANT_DOCS: DocLink[] = [
  {
    title: 'Argo IQ — Product site',
    description: 'The public face: positioning, AQI, and the operating-layer pitch.',
    url: 'https://argoiq.com',
  },
  {
    title: 'Proposal reference',
    description: 'The branded proposal layout our generated docs match.',
    url: 'https://proposals.argoiq.com/fusiontaxes/',
  },
  {
    title: 'Persona reference',
    description: 'The buyer-persona format the persona creator targets.',
    url: 'https://trenton.personas.argoiq.com/',
  },
  {
    title: 'Argo OS workspace',
    description: 'This app — the in-house super-CRM and agent control plane.',
    url: 'https://os.argoiq.com',
  },
];

const STAGE_META: Record<RoadmapStage, { label: string; accent: string }> = {
  now: { label: 'Now', accent: C.magenta },
  next: { label: 'Next', accent: C.blueElectric },
  later: { label: 'Later', accent: C.textMuted },
  shipped: { label: 'Shipped', accent: C.green },
};

const STAGE_ORDER: RoadmapStage[] = ['now', 'next', 'later', 'shipped'];

const STATUS_META: Record<
  MilestoneStatus,
  { label: string; pill: string; icon: LucideIcon; color: string }
> = {
  planned: { label: 'Planned', pill: 'glass-pill', icon: CircleDashed, color: C.textMuted },
  'in-progress': {
    label: 'In progress',
    pill: 'glass-pill glass-pill--blue',
    icon: CircleDot,
    color: C.blueElectric,
  },
  blocked: {
    label: 'Blocked',
    pill: 'glass-pill glass-pill--orange',
    icon: AlertTriangle,
    color: C.orangeWarm,
  },
  done: {
    label: 'Done',
    pill: 'glass-pill glass-pill--green',
    icon: CheckCircle2,
    color: C.green,
  },
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  // Allow loose values like "2026-Q4".
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtRange(start?: string, target?: string): string {
  if (start && target) return `${fmtDate(start)} → ${fmtDate(target)}`;
  if (target) return `Target ${fmtDate(target)}`;
  if (start) return `From ${fmtDate(start)}`;
  return 'Unscheduled';
}

// ===========================================================================

/** One row in the auto-derived "This week" panel. */
interface WeekRow {
  id: string;
  kind: 'ticket' | 'task';
  title: string;
  owner: string;
  badge: string;
  accent: string;
  // Tracked separately from `badge`: a blocked item that is *also* due soon
  // shows a "due …" badge, so the blocked-first sort can't infer it from the
  // badge text — it must read this flag to keep blocked work at the top.
  blocked: boolean;
}

export function DevelopmentView({ onOpenAgents }: { onOpenAgents?: () => void } = {}) {
  const { user } = useAuth();
  // Viewers never see write affordances — hide, don't error.
  const canMutate = user?.role !== 'viewer';
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);

  useEffect(() => {
    let active = true;
    bootstrapRoadmapIfEmpty().catch(() => undefined);
    const unsubs = [
      subscribeRoadmap((records) => {
        if (active) setItems(records);
      }),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeTasks((r) => active && setTasks(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  // "This week" derives itself from the live boards — work that is in
  // motion (claimed / in-progress / review tickets) plus anything due in
  // the next 7 days. Nobody curates this list; it is always current, which
  // is the difference between a status page and a status *report*.
  const thisWeek = useMemo<WeekRow[]>(() => {
    const rows: WeekRow[] = [];
    const weekOut = Date.now() + 7 * 24 * 60 * 60 * 1000;
    for (const t of tickets) {
      const moving = t.status === 'claimed' || t.status === 'in-progress' || t.status === 'review';
      const dueSoon =
        t.deadline !== null &&
        Date.parse(t.deadline) <= weekOut &&
        t.status !== 'done' &&
        t.status !== 'cancelled';
      if (!moving && !dueSoon) continue;
      rows.push({
        id: `t-${t.id}`,
        kind: 'ticket',
        title: `${t.id} · ${t.title}`,
        owner: t.owner ?? 'unclaimed',
        badge: t.status === 'blocked' ? 'blocked' : t.status,
        accent:
          t.status === 'blocked'
            ? C.orangeWarm
            : t.status === 'review'
              ? C.green
              : C.blueElectric,
        blocked: t.status === 'blocked',
      });
    }
    for (const t of tasks) {
      if (t.status === 'done') continue;
      const dueSoon = t.dueDate && Date.parse(t.dueDate) <= weekOut;
      if (t.status !== 'in-progress' && !dueSoon) continue;
      rows.push({
        id: `k-${t.id}`,
        kind: 'task',
        title: t.title,
        owner: t.assignee ?? 'unassigned',
        badge: dueSoon && t.dueDate ? `due ${fmtDate(t.dueDate)}` : t.status,
        accent: t.status === 'blocked' ? C.orangeWarm : C.magentaSoft,
        blocked: t.status === 'blocked',
      });
    }
    // Blocked first, then tickets before tasks, then alphabetical — the
    // ordering itself says "unstick things, ship things, then chores".
    rows.sort((a, b) => {
      if (a.blocked !== b.blocked) return a.blocked ? -1 : 1;
      if (a.kind !== b.kind) return a.kind === 'ticket' ? -1 : 1;
      return a.title.localeCompare(b.title);
    });
    return rows;
  }, [tickets, tasks]);

  const byStage = useMemo(() => {
    const map: Record<RoadmapStage, RoadmapItem[]> = {
      now: [],
      next: [],
      later: [],
      shipped: [],
    };
    for (const it of items) map[it.stage]?.push(it);
    for (const stage of STAGE_ORDER) {
      map[stage].sort((a, b) => {
        const ap = a.targetDate ?? '9999';
        const bp = b.targetDate ?? '9999';
        return ap < bp ? -1 : ap > bp ? 1 : 0;
      });
    }
    return map;
  }, [items]);

  const shippedCount = byStage.shipped.length;
  const activeCount = byStage.now.length;

  return (
    // The Projects shell is height-locked (app-shell--fixed, overflow:hidden)
    // for the kanban's sake — so this tab must own its own vertical scroll
    // or the roadmap below the fold is unreachable.
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxHeight: 'calc(100vh - var(--topmenu-h) - 96px)',
        overflowY: 'auto',
        paddingRight: 4,
      }}
    >
      {/* North Star */}
      <GlassPanel variant="magenta" animate={false} style={{ padding: '22px 24px' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              flex: '0 0 auto',
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.28), rgba(187,65,152,0.14))',
              border: '1px solid rgba(255,255,255,0.16)',
              color: '#fff',
            }}
          >
            <Compass size={22} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span className="t-eyebrow">North Star</span>
            <p
              className="t-section-title"
              style={{ margin: '4px 0 0', fontWeight: 700, lineHeight: 1.35 }}
            >
              {NORTH_STAR}
            </p>
            <p className="t-meta" style={{ margin: '8px 0 0', color: C.textMuted }}>
              {activeCount} milestone{activeCount === 1 ? '' : 's'} in flight ·{' '}
              {shippedCount} shipped
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* This week — auto-derived from the live ticket + task boards.
          No one maintains this list; it maintains itself. */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarClock size={15} style={{ color: C.magentaSoft }} />
            <h2 className="t-section-title" style={{ margin: 0 }}>
              This week
            </h2>
          </div>
          <span className="glass-pill" title="Derived live from tickets in motion and tasks due within 7 days — never manually curated.">
            auto-updating
          </span>
        </div>
        <p className="t-meta" style={{ margin: '4px 0 10px', color: C.textMuted }}>
          Tickets in motion + everything due in the next 7 days, straight from the boards.
        </p>
        {thisWeek.length === 0 ? (
          <p className="t-meta" style={{ margin: 0 }}>
            Nothing in motion or due this week — claim a ticket or schedule a task and it appears here.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {thisWeek.slice(0, 10).map((row) => (
              <div
                key={row.id}
                className="glass-row"
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    flex: '0 0 auto',
                    background: row.accent,
                    boxShadow: `0 0 8px ${row.accent}`,
                  }}
                />
                <span className="t-card-title" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.title}
                </span>
                <span className="t-meta" style={{ flex: '0 0 auto' }}>{row.owner}</span>
                <span className="glass-pill" style={{ flex: '0 0 auto' }}>{row.badge}</span>
              </div>
            ))}
            {thisWeek.length > 10 && (
              <span className="t-meta">+ {thisWeek.length - 10} more on the boards</span>
            )}
          </div>
        )}
      </GlassPanel>

      {/* Roadmap */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MapIcon size={15} style={{ color: C.blueElectric }} />
          <h2 className="t-section-title" style={{ margin: 0 }}>
            Roadmap
          </h2>
        </div>
        <p className="t-meta" style={{ margin: '4px 0 0', color: C.textMuted }}>
          Stages, goals, and milestones with owners and timelines.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 14,
            marginTop: 16,
            alignItems: 'start',
          }}
        >
          {STAGE_ORDER.map((stage) => {
            const meta = STAGE_META[stage];
            const stageItems = byStage[stage];
            return (
              <div key={stage} style={{ display: 'grid', gap: 10 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingBottom: 6,
                    borderBottom: `2px solid ${meta.accent}`,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 99,
                      background: meta.accent,
                    }}
                  />
                  <span
                    className="t-card-title"
                    style={{ color: C.text, fontWeight: 700 }}
                  >
                    {meta.label}
                  </span>
                  <span className="t-meta" style={{ color: C.textMuted }}>
                    {stageItems.length}
                  </span>
                </div>

                {stageItems.length === 0 ? (
                  <p
                    className="t-meta"
                    style={{ margin: 0, color: C.textDim, fontStyle: 'italic' }}
                  >
                    Nothing here yet.
                  </p>
                ) : (
                  stageItems.map((it) => (
                    <MilestoneCard
                      key={it.id}
                      item={it}
                      tickets={tickets}
                      tasks={tasks}
                      canMutate={canMutate}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </GlassPanel>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* Important docs */}
        <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={15} style={{ color: C.orangeWarm }} />
            <h2 className="t-section-title" style={{ margin: 0 }}>
              Important docs
            </h2>
          </div>
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            {IMPORTANT_DOCS.map((d) => (
              <a
                key={d.url}
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-row"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 10,
                  textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    className="t-body"
                    style={{ display: 'block', color: C.text, fontWeight: 600 }}
                  >
                    {d.title}
                  </span>
                  <span className="t-meta" style={{ display: 'block', color: C.textMuted }}>
                    {d.description}
                  </span>
                </span>
                <ExternalLink size={13} style={{ color: C.textMuted, flex: '0 0 auto', marginTop: 2 }} />
              </a>
            ))}
          </div>
        </GlassPanel>

        {/* Agent workers */}
        <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Cpu size={15} style={{ color: C.magenta }} />
            <h2 className="t-section-title" style={{ margin: 0 }}>
              Agent workers
            </h2>
          </div>
          <p className="t-meta" style={{ margin: '4px 0 0', color: C.textMuted }}>
            Network seams the app routes real work through when configured.
          </p>
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            <WorkerRow
              name="Claude agent worker"
              detail="Chat, autopilot, and deployed agents."
              info={argoWorker.workerInfo()}
              envVar="VITE_AGENT_API_URL"
            />
            <WorkerRow
              name="Prospect enrichment"
              detail="LinkedIn + website evaluation."
              info={enrich.enrichInfo()}
              envVar="VITE_ENRICH_API_URL"
            />
            <WorkerRow
              name="Google Workspace"
              detail="Pulls Google Meet notes into the timeline."
              info={meetNotes.meetInfo()}
              envVar="VITE_GSUITE_API_URL"
            />
            <WorkerRow
              name="Slack notifications"
              detail="Posts digests and alerts to Slack channels."
              info={slackNotify.slackInfo()}
              envVar="VITE_SLACK_API_URL"
            />
          </div>
        </GlassPanel>

        {/* Your agents — the deployed fleet with live ledger receipts, so
            the Dev hub shows the governed workers next to their seams. */}
        {onOpenAgents && <AgentFleetWidget onOpen={onOpenAgents} />}
      </div>
    </div>
  );
}

// --- Milestone card ---------------------------------------------------------

function MilestoneCard({
  item,
  tickets,
  tasks,
  canMutate,
}: {
  item: RoadmapItem;
  tickets: Ticket[];
  tasks: ProjectTask[];
  canMutate: boolean;
}) {
  const status = STATUS_META[item.status];
  const StatusIcon = status.icon;
  const [linking, setLinking] = useState(false);

  // Auto progress from linked tickets/tasks; manual estimate otherwise.
  const rollup = useMemo(
    () => milestoneRollup(item, tickets, tasks),
    [item, tickets, tasks],
  );
  const progress = milestoneProgress(item, rollup);

  return (
    <GlassPanel variant="sharp" animate={false} style={{ padding: 14 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <span className="t-card-title" style={{ color: C.text, fontWeight: 600 }}>
            {item.title}
          </span>
          <span
            className={status.pill}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}
          >
            <StatusIcon size={10} /> {status.label}
          </span>
        </div>

        {item.goal && (
          <span
            className="t-meta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.textMuted }}
          >
            <Sparkles size={11} /> {item.goal}
          </span>
        )}

        {item.description && (
          <p className="t-meta" style={{ margin: 0, color: C.textMuted, lineHeight: 1.5 }}>
            {item.description}
          </p>
        )}

        {/* Linked work — the tickets/tasks this milestone rolls up. */}
        {rollup && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {rollup.refs.map((ref) => (
              <LinkedWorkChip key={`${ref.kind}-${ref.id}`} workRef={ref} />
            ))}
          </div>
        )}

        {/* Progress bar */}
        <div
          aria-hidden="true"
          style={{
            height: 5,
            borderRadius: 99,
            background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: status.color,
              transition: 'width 240ms ease',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            color: C.textMuted,
          }}
        >
          {item.assignee && (
            <span className="t-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <UserIcon size={11} /> {item.assignee}
            </span>
          )}
          <span className="t-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={11} /> {fmtRange(item.startDate, item.targetDate)}
          </span>
          {canMutate && (
            <button
              type="button"
              className="glass-btn"
              onClick={() => setLinking((v) => !v)}
              title="Link tickets and tasks — progress then auto-computes from their completion."
              style={{ padding: '2px 8px' }}
            >
              <Link2 size={11} /> Link work
            </button>
          )}
          <span
            className="t-meta"
            style={{ marginLeft: 'auto', color: status.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            {rollup && (
              <span
                className="glass-pill"
                title={`Auto-computed: ${rollup.done} of ${rollup.total} linked item${rollup.total === 1 ? '' : 's'} done. The manual estimate is ignored while links exist.`}
              >
                auto · {rollup.done}/{rollup.total}
              </span>
            )}
            {progress}%
          </span>
        </div>

        {linking && canMutate && (
          <LinkWorkEditor
            item={item}
            tickets={tickets}
            tasks={tasks}
            onClose={() => setLinking(false)}
          />
        )}
      </div>
    </GlassPanel>
  );
}

/** Status accent for a linked-work chip dot. */
function refAccent(ref: LinkedWorkRef): string {
  if (ref.done) return C.green;
  if (ref.status === 'blocked') return C.orangeWarm;
  if (ref.status === 'cancelled') return C.textDim;
  if (ref.status === 'todo' || ref.status === 'backlog' || ref.status === 'open')
    return C.textMuted;
  return C.blueElectric; // claimed / in-progress / review — in motion
}

/** One linked ticket/task chip: kind icon, status dot, label. */
function LinkedWorkChip({ workRef }: { workRef: LinkedWorkRef }) {
  const KindIcon = workRef.kind === 'ticket' ? TicketIcon : ListChecks;
  return (
    <span
      className="glass-pill"
      title={`${workRef.kind === 'ticket' ? 'Ticket' : 'Task'} — ${workRef.status}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        maxWidth: 220,
        opacity: workRef.status === 'cancelled' ? 0.6 : 1,
      }}
    >
      <KindIcon size={10} style={{ flex: '0 0 auto' }} />
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: 99,
          flex: '0 0 auto',
          background: refAccent(workRef),
        }}
      />
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: workRef.done ? 'line-through' : undefined,
        }}
      >
        {workRef.label}
      </span>
    </span>
  );
}

// --- Link-work editor -------------------------------------------------------

/**
 * Inline editor for a milestone's linked tickets/tasks. Checkbox rows
 * over the live boards with a shared filter; Save persists both id
 * lists in one update. Deliberately inline (not a modal) so linking
 * stays a glance-and-tick action on the card itself.
 */
function LinkWorkEditor({
  item,
  tickets,
  tasks,
  onClose,
}: {
  item: RoadmapItem;
  tickets: Ticket[];
  tasks: ProjectTask[];
  onClose: () => void;
}) {
  const [ticketIds, setTicketIds] = useState<Set<string>>(
    () => new Set(item.linkedTicketIds ?? []),
  );
  const [taskIds, setTaskIds] = useState<Set<string>>(
    () => new Set(item.linkedTaskIds ?? []),
  );
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  // Linked first, then unfinished, then label — keeps the relevant rows
  // on top of what can be a long board.
  const q = query.trim().toLowerCase();
  const ticketRows = useMemo(() => {
    const rows = tickets.filter(
      (t) => !q || `${t.id} ${t.title}`.toLowerCase().includes(q),
    );
    return [...rows].sort((a, b) => {
      const al = ticketIds.has(a.id) ? 0 : 1;
      const bl = ticketIds.has(b.id) ? 0 : 1;
      if (al !== bl) return al - bl;
      const ad = a.status === 'done' || a.status === 'cancelled' ? 1 : 0;
      const bd = b.status === 'done' || b.status === 'cancelled' ? 1 : 0;
      if (ad !== bd) return ad - bd;
      return a.id.localeCompare(b.id);
    });
  }, [tickets, q, ticketIds]);
  const taskRows = useMemo(() => {
    const rows = tasks.filter(
      (t) => !q || t.title.toLowerCase().includes(q),
    );
    return [...rows].sort((a, b) => {
      const al = taskIds.has(a.id) ? 0 : 1;
      const bl = taskIds.has(b.id) ? 0 : 1;
      if (al !== bl) return al - bl;
      const ad = a.status === 'done' ? 1 : 0;
      const bd = b.status === 'done' ? 1 : 0;
      if (ad !== bd) return ad - bd;
      return a.title.localeCompare(b.title);
    });
  }, [tasks, q, taskIds]);

  const toggle = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const save = async () => {
    setBusy(true);
    try {
      await updateRoadmapItem(item.id, {
        linkedTicketIds: [...ticketIds],
        linkedTaskIds: [...taskIds],
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gap: 8,
        padding: 10,
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.10)',
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <input
        className="glass-input"
        placeholder="Filter tickets and tasks…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div style={{ display: 'grid', gap: 8 }}>
        <LinkPickList
          label="Tickets"
          icon={TicketIcon}
          empty={ticketRows.length === 0}
        >
          {ticketRows.slice(0, 30).map((t) => (
            <LinkPickRow
              key={t.id}
              checked={ticketIds.has(t.id)}
              label={`${t.id} · ${t.title}`}
              status={t.status}
              onToggle={() => setTicketIds((s) => toggle(s, t.id))}
            />
          ))}
        </LinkPickList>
        <LinkPickList label="Tasks" icon={ListChecks} empty={taskRows.length === 0}>
          {taskRows.slice(0, 30).map((t) => (
            <LinkPickRow
              key={t.id}
              checked={taskIds.has(t.id)}
              label={t.title}
              status={t.status}
              onToggle={() => setTaskIds((s) => toggle(s, t.id))}
            />
          ))}
        </LinkPickList>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button type="button" className="glass-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="glass-btn glass-btn--primary"
          onClick={save}
          disabled={busy}
        >
          <Link2 size={11} /> Save links
        </button>
      </div>
    </div>
  );
}

function LinkPickList({
  label,
  icon: Icon,
  empty,
  children,
}: {
  label: string;
  icon: LucideIcon;
  empty: boolean;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span
        className="t-eyebrow"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
      >
        <Icon size={11} /> {label}
      </span>
      {empty ? (
        <span className="t-meta" style={{ color: C.textDim }}>
          No matches.
        </span>
      ) : (
        <div style={{ display: 'grid', gap: 2, maxHeight: 150, overflowY: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function LinkPickRow({
  checked,
  label,
  status,
  onToggle,
}: {
  checked: boolean;
  label: string;
  status: string;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '3px 4px',
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span
        className="t-meta"
        style={{
          color: checked ? C.text : C.textMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </span>
      <span className="glass-pill" style={{ flex: '0 0 auto' }}>
        {status}
      </span>
    </label>
  );
}

// --- Worker status row ------------------------------------------------------

function WorkerRow({
  name,
  detail,
  info,
  envVar,
}: {
  name: string;
  detail: string;
  info: { configured: boolean; baseUrl: string | null };
  envVar: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <span style={{ minWidth: 0, flex: 1 }}>
        <span className="t-body" style={{ display: 'block', color: C.text, fontWeight: 600 }}>
          {name}
        </span>
        <span className="t-meta" style={{ display: 'block', color: C.textMuted }}>
          {detail}
        </span>
        <span
          className="t-meta"
          style={{ display: 'block', color: C.textDim, marginTop: 2, fontFamily: 'var(--font-mono, monospace)' }}
        >
          {info.configured ? info.baseUrl : `Set ${envVar} to connect`}
        </span>
      </span>
      <span
        className={info.configured ? 'glass-pill glass-pill--green' : 'glass-pill'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}
      >
        {info.configured ? <Sparkles size={11} /> : <CircleDashed size={11} />}
        {info.configured ? 'Connected' : 'Simulation'}
      </span>
    </div>
  );
}
